import React from 'react';
import Head from 'next/head';
import Image from 'next/legacy/image';
import { gql } from '@apollo/client';

import Error from 'next/error';
import { useRouter } from 'next/router';
import {
  client,
  getAllHowItWorksTemplatesBySlug,
  getHowItWorksTemplateBySlug
} from '../../apollo-client';
import { BodyRegular, H1, H2, H3, H4 } from '@components/atoms/typography';
import Button from '@components/atoms/button';
import Icon from '@components/atoms/icon';
import ReviewsCarousel from '@components/reviews-carousel';
import { getYoastHeadSEO } from '@lib/utils';

const HowItWorksTemplate = ({ pageData }) => {
  const data = pageData?.howItWorksTemplateData;

  if (!data) {
    <Error statusCode={404} />;
  }

  const router = useRouter();
  // asPath, not pathname: on a dynamic route pathname is the pattern
  // (`/how-it-works/[slug]`), which would become the canonical URL.
  const yoastHead = getYoastHeadSEO(pageData?.seo?.fullHead, router.asPath);

  return (
    <>
      <Head>{yoastHead}</Head>
      <main className="bg-white space-y-5 md:space-y-10">
        <div className="container mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 py-10 mb-5">
            <div className="flex flex-col justify-center px-2 md:px-12">
              <H1 className="max-w-[520px] mb-3">{data.pageTitle}</H1>
              <BodyRegular className="max-w-[450px] mb-5">
                {data.pageSubtitle}
              </BodyRegular>
              <Button href="/join" isLink variant="primary">
                Join The Hub
              </Button>
            </div>
            <div className="px-2 md:px-5 flex items-center">
              <iframe
                className="w-full aspect-video rounded-lg"
                src={data.youtubeEmbedVideoUrl}
              ></iframe>
            </div>
          </div>
          <div>
            <H3 className="text-center">How does it work?</H3>
            <div className="py-5 md:py-10 flex flex-col md:flex-row justify-evenly items-center md:items-start space-y-5 md:space-y-0">
              {data.howItWorksStep.map(({ header, description }, index) => (
                <div
                  key={description}
                  className="flex flex-col space-y-4 justify-center max-w-[250px] items-center"
                >
                  <H3
                    textColor="text-neutral-50"
                    className="flex items-center justify-center bg-primary-600 w-[48px] h-[48px] rounded-full"
                  >
                    {index + 1}
                  </H3>
                  <H4 className="text-center">{header}</H4>
                  <BodyRegular className="text-center">
                    {description}
                  </BodyRegular>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-primary-100 py-5 md:py-12 flex justify-center items-center flex-col px-5">
          <H2 className="mb-2">{data.joinHeader}</H2>
          <BodyRegular className="mb-7 text-center">
            {data.joinDescription}
          </BodyRegular>
          <Button href="/join" isLink variant="primary">
            Join The Hub
          </Button>
        </div>
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 py-10 px-2 md:px-8">
            <div className="flex justify-center">
              <Image
                width={300}
                height={520}
                src={data.myTeamImage?.node?.sourceUrl}
                alt="My team screenshot"
                objectFit="contain"
                // WP Engine 403s Vercel's optimiser on cache-miss (FFH-1688)
                unoptimized
              />
            </div>
            <div className="flex flex-col space-y-8 px-5 md:px-10">
              <H2>{data.myTeamHeaderText}</H2>
              <BodyRegular>{data.myTeamDescriptionText}</BodyRegular>
              {data.featureStep.map(({ header, description, iconName }) => (
                <div key={description} className="flex flex-col">
                  <div className="flex items-center mb-4">
                    <Icon className="w-6 h-6 mr-2" name={iconName} />
                    <H4>{header}</H4>
                  </div>
                  <BodyRegular className="pr-3 md:pr-10">
                    {description}
                  </BodyRegular>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <ReviewsCarousel
            header="We make winning at fantasy football a reality"
            subHeader="Fantasy football is more popular than ever. But to combat the competition, you need an edge. Let our team of world-class experts guide your decisions every gameweek and start dominating your mini-leagues."
          />
        </div>
        <div className="flex flex-col items-center justify-center px-5 py-10 md:py-16 space-y-5">
          <H2 className="text-center">{data.bottomJoinHeader}</H2>
          <Button href="/join" isLink variant="primary">
            Join The Hub today
          </Button>
        </div>
      </main>
    </>
  );
};

export default HowItWorksTemplate;

export async function getStaticPaths() {
  const { data } = await client.query({
    query: gql`
      ${getAllHowItWorksTemplatesBySlug()}
    `
  });

  const paths = data.howItWorksTemplates.nodes.map(({ slug }) => ({
    params: { slug }
  }));

  return {
    paths: paths || [],
    fallback: false
  };
}

export async function getStaticProps({ params }) {
  const { data } = await client.query({
    query: gql`
      ${getHowItWorksTemplateBySlug(params?.slug)}
    `
  });

  return {
    props: {
      pageData: data.howItWorksTemplateBy
    },
    revalidate: 3000 // Rebuild the page every 5 minutes to update the content
  };
}
